import { AI_PRODUCT_NAME } from "src/common/branding.js";
import type { CompanyDomain } from "src/common/company-domain/types.js";

// WHY kontakta-oss: activating a product is a sales conversation until a license server exists.
export const SALES_CONTACT_URL = "https://www.exopen.se/kontakta-oss";

export type ProductKey =
	| "cloudPlatform"
	| "intelligenceLayer"
	| "planning"
	| "ai"
	| "excel"
	| "powerBi";

export interface ProductPathContext {
	legalEntityId?: string | undefined;
}

const scopedPath = (
	companyDomainId: string,
	segment: string,
	legalEntityId?: string | undefined,
) =>
	legalEntityId
		? `/company-domains/${companyDomainId}/legal-entities/${legalEntityId}/${segment}`
		: `/company-domains/${companyDomainId}/${segment}`;

export interface ProductConfig {
	key: ProductKey;
	/** Brand name — shown verbatim in every language. */
	name: string;
	path: (
		companyDomain: Pick<CompanyDomain, "id">,
		context?: ProductPathContext,
	) => string;
	/** Only for products with a self-service, in-portal activation flow — the rest activate through sales. */
	activatePath?: ((companyDomain: CompanyDomain) => string) | undefined;
	/** Where a super admin switches the product on for a domain, until a license server takes over. */
	superAdminActivatePath?:
		| ((companyDomain: CompanyDomain) => string)
		| undefined;
}

/** The one list of Exopen products — tiles, nav items and route guards all read from here. */
export const PRODUCTS: readonly ProductConfig[] = [
	{
		key: "cloudPlatform",
		name: "Cloud Platform",
		path: (companyDomain, context) =>
			scopedPath(companyDomain.id, "data-sources", context?.legalEntityId),
	},
	{
		key: "intelligenceLayer",
		name: "Intelligence Layer",
		path: (companyDomain) =>
			`/company-domains/${companyDomain.id}/intelligence-layer`,
	},
	{
		key: "planning",
		name: "Planning",
		path: (companyDomain, context) =>
			scopedPath(companyDomain.id, "planning", context?.legalEntityId),
		// WHY: the plans page explains how to get Planning switched on, so "Aktivera" lands there.
		activatePath: (companyDomain) =>
			`/company-domains/${companyDomain.id}/planning`,
		superAdminActivatePath: (companyDomain) =>
			`/company-domains/${companyDomain.id}/internal/dev-tools/planning`,
	},
	{
		key: "ai",
		name: AI_PRODUCT_NAME,
		path: (companyDomain) => `/company-domains/${companyDomain.id}/ai-chat`,
		activatePath: (companyDomain) =>
			`/company-domains/${companyDomain.id}/ai-chat`,
	},
	{
		key: "excel",
		name: "Exopen for Excel",
		// The v7 report picker; users without v7 access go to EWR instead — see getExcelTarget.
		path: (companyDomain) =>
			`/company-domains/${companyDomain.id}/excel-reports`,
	},
	{
		key: "powerBi",
		name: "Exopen for Power BI",
		path: (companyDomain) =>
			`/company-domains/${companyDomain.id}/powerbi-reports`,
		// WHY: the reports page is where a company admin connects Power BI themselves.
		activatePath: (companyDomain) =>
			`/company-domains/${companyDomain.id}/powerbi-reports`,
	},
];

export const getProduct = (key: ProductKey): ProductConfig => {
	const product = PRODUCTS.find((candidate) => candidate.key === key);
	if (!product) throw new Error(`Unknown product: ${key}`);
	return product;
};

/** Where "Exopen for Excel" leads: v7's report picker in the portal, or the legacy EWR site outside it. */
export type ExcelTarget =
	| { kind: "reportPicker"; path: string }
	| { kind: "ewr"; url: string };

export interface ExcelAccessInput {
	/** From `useHasExcelReportAccess`. */
	hasExcelReportAccess: boolean;
	/** The domain's EWR address from the report status API, null when it has none. */
	ewrUrl: string | null;
}

/** v7 access wins over an EWR address, so v7 customers that still carry an EWR row never land on EWR. */
export const getExcelTarget = (
	companyDomain: Pick<CompanyDomain, "id">,
	{ hasExcelReportAccess, ewrUrl }: ExcelAccessInput,
): ExcelTarget | null => {
	if (hasExcelReportAccess) {
		return {
			kind: "reportPicker",
			path: getProduct("excel").path(companyDomain),
		};
	}
	if (ewrUrl) return { kind: "ewr", url: ewrUrl };
	return null;
};
